import { Clock, Loader2, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useOperations } from '../api/hooks';
import { StatusDot } from '../components/Status';
import { TableScroll } from '../components/TableScroll';
import { bytes, relative } from '../lib/format';
import { RESTORE_STATUS_LABEL } from '../components/ArchiveRestore';

/** Queues, jobs, storage agents, storage and recent failures at a glance (Monitor, Operations tab). */
export function Operations() {
  const q = useOperations();
  const navigate = useNavigate();
  const d = q.data;
  if (!d) return <div className="page"><Loader2 className="spin" /></div>;
  const jobRow = (k: keyof typeof d.jobs, label: string) => {
    const c = d.jobs[k] ?? {};
    // Queued (0.95.0): active jobs whose next step waits for a free worker.
    const queued = (d.waiting as Record<string, number> | undefined)?.[k] ?? 0;
    const active = Object.entries(c).filter(([s]) => !['succeeded', 'failed', 'cancelled', 'ready', 'partial', 'completed'].includes(s)).reduce((n, [, v]) => n + v, 0);
    return (
      <tr key={k}>
        <td className="wrap" style={{ fontWeight: 560 }}>{label}</td>
        <td className="mono nowrap">{active}{queued > 0 && <span className="faint" data-testid={`ops-queued-${k}`} title="Waiting for a free worker"> · {queued} queued</span>}</td>
        <td className="mono">{(c.succeeded ?? 0) + (c.ready ?? 0) + (c.completed ?? 0)}</td>
        <td className="mono" style={{ color: (c.failed ?? 0) + (c.partial ?? 0) ? 'var(--danger)' : undefined }}>{(c.failed ?? 0) + (c.partial ?? 0)}</td>
      </tr>
    );
  };
  return (
    <div className="page" data-testid="operations">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <h2 style={{ flex: 1 }}>Operations</h2>
        <button className="btn ghost sm" onClick={() => void q.refetch()}><RefreshCw size={13} /> Refresh</button>
      </div>
      <p className="lede">Worker queues report their pollers every few seconds; a queue with no pollers means its work will wait until a worker for that site comes back.</p>
      <div className="ops-grid">
        <section className="panel-card">
          <p className="section-label">Task queues</p>
          <table className="table compact">
            <thead><tr><th>Queue</th><th>Pollers</th><th>Last poll</th></tr></thead>
            <tbody>{d.queues.map((qq) => (
              <tr key={qq.taskQueue}>
                <td className="mono wrap">{qq.taskQueue}</td>
                <td className="nowrap">{qq.stale || qq.pollers === 0 ? <StatusDot tone="failed">{qq.stale ? 'unknown' : 'none'}</StatusDot> : <StatusDot tone="ready">{qq.pollers}</StatusDot>}</td>
                <td className="muted nowrap">{relative(qq.lastPollAt)}</td>
              </tr>
            ))}{d.queues.length === 0 && <tr><td colSpan={3} className="muted">No reports yet.</td></tr>}</tbody>
          </table>
        </section>
        <section className="panel-card" data-testid="ops-ingests">
          <p className="section-label">Ingests (last 24 h)</p>
          <table className="table compact">
            <thead><tr><th>Kind</th><th>Active</th><th>Done</th><th>Failed</th></tr></thead>
            <tbody><tr>
              <td style={{ fontWeight: 560 }}>Files coming in</td>
              <td className="mono" data-testid="ops-ingests-active">{d.ingests.active}</td>
              <td className="mono" data-testid="ops-ingests-done">{d.ingests.done}</td>
              <td className="mono" data-testid="ops-ingests-failed" style={{ color: d.ingests.failed ? 'var(--danger)' : undefined }}>{d.ingests.failed}</td>
            </tr></tbody>
          </table>
          <span className="help">The same numbers as Monitor, Ingest: one per file, whatever brought it. Partly processed files count as failed.</span>
        </section>
        <section className="panel-card">
          <p className="section-label">Processing runs and jobs (last 24 h)</p>
          <table className="table compact">
            <thead><tr><th>Kind</th><th>Active</th><th>Done</th><th>Failed</th></tr></thead>
            <tbody>{jobRow('ingest', 'Processing runs')}{jobRow('transfers', 'Transfers')}{jobRow('exports', 'Exports')}{jobRow('automations', 'Automations')}{Object.keys(d.jobs.restores ?? {}).length > 0 && jobRow('restores', 'Restores from archives')}{Object.keys(d.jobs.qc ?? {}).length > 0 && jobRow('qc', 'QC runs (done: checked; failed: could not run)')}</tbody>
          </table>
        </section>
        <section className="panel-card">
          <p className="section-label">Storage agents</p>
          <table className="table compact">
            <thead><tr><th>Agent</th><th>Site</th><th>State</th></tr></thead>
            <tbody>{d.agents.map((a) => (
              <tr key={a.name}><td className="wrap">{a.name}</td><td className="muted wrap">{a.site}</td>
                <td>{a.connected ? <StatusDot tone="ready">Connected</StatusDot> : <StatusDot tone="offline">{a.status}</StatusDot>}</td></tr>
            ))}{d.agents.length === 0 && <tr><td colSpan={3} className="muted">No agents registered.</td></tr>}</tbody>
          </table>
        </section>
        <section className="panel-card">
          <p className="section-label">Storage</p>
          <table className="table compact">
            <thead><tr><th>Location</th><th>State</th><th>Files</th><th>Size</th></tr></thead>
            <tbody>{d.storage.map((s) => (
              <tr key={s.name}><td className="wrap">{s.name} <span className="faint">{s.kind}</span></td>
                <td><span className={`avail ${s.availability}`}><span className="dot" />{s.availability}</span></td>
                <td className="mono">{s.files}</td><td className="mono">{bytes(s.bytes)}</td></tr>
            ))}</tbody>
          </table>
        </section>
      </div>
      {(d.restores?.length ?? 0) > 0 && (
        <section className="panel-card" style={{ marginTop: 16 }} data-testid="ops-restores">
          <p className="section-label">Restores from archives (active and last 24 h)</p>
          <TableScroll>
          <table className="table compact">
            <thead><tr><th>Asset</th><th>From</th><th>State</th><th>Retrieval</th><th>Requested</th></tr></thead>
            <tbody>{d.restores!.map((r) => (
              <tr key={r.id} tabIndex={0} onClick={() => navigate(`/assets/${r.assetId}`)} data-status={r.status}>
                <td className="wrap" style={{ minWidth: 160 }}>{r.assetTitle} <span className="faint mono">{r.assetCode}</span></td>
                <td className="wrap">{r.source.name}{r.source.storageClass ? <span className="faint"> · {r.source.storageClass}</span> : null}</td>
                <td className="wrap" style={{ minWidth: 120 }}>{r.status === 'failed' ? <span className="warn-text">{r.error?.message ?? 'Failed'}</span> : RESTORE_STATUS_LABEL[r.status]}</td>
                <td className="muted wrap">{r.tier ?? 'Archive'} · usually {r.expectedDuration}</td>
                <td className="muted wrap"><Clock size={11} /> {relative(r.createdAt)}{r.requestedBy ? ` by ${r.requestedBy}` : ''}</td>
              </tr>
            ))}</tbody>
          </table>
          </TableScroll>
        </section>
      )}
      <section className="panel-card" style={{ marginTop: 16 }}>
        <p className="section-label">Recent failures (last 24 h)</p>
        {d.failures.length === 0 ? <p className="muted">No failures in the last 24 hours.</p> : (
          <TableScroll>
          <table className="table compact" data-testid="failures">
            <thead><tr><th>Kind</th><th>Subject</th><th>Error</th><th>When</th></tr></thead>
            <tbody>{d.failures.map((f) => (
              <tr key={`${f.kind}-${f.subjectId}`} tabIndex={0} onClick={() => navigate(f.kind === 'automation' ? `/workflows/runs/${f.linkId}` : f.kind === 'export' ? `/create/${f.linkId}` : `/assets/${f.linkId}`)}>
                <td className="muted nowrap">{f.kind}</td><td className="wrap" style={{ minWidth: 140 }}>{f.subject}</td><td className="warn-text wrap" style={{ minWidth: 200 }}>{f.error?.message ?? '—'}</td><td className="muted nowrap"><Clock size={11} /> {relative(f.at)}</td>
              </tr>
            ))}</tbody>
          </table>
          </TableScroll>
        )}
      </section>
    </div>
  );
}
